import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSeasonsByAnimeId, fetchSeasonsBySeriesId, deleteSeason, updateSeason, addSeason } from '../redux/slices/seasonSlice';
import { fetchAnimes } from '../redux/slices/animeSlice';
import { fetchSeries } from '../redux/slices/seriesSlice';
import ConfirmModal from './ConfirmModal';
import EditSeasonModal from './EditSeasonModal';
import { Plus } from 'lucide-react';
import axios from 'axios';

const SeasonTable = ({ seriesMode = false }) => {
  const dispatch = useDispatch();
  const { items: animes } = useSelector((state) => state.animes);
  const { items: series } = useSelector((state) => state.series);
  const { items: seasons, status, error } = useSelector((state) => state.seasons);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedContentId, setSelectedContentId] = useState('');
  const [editingSeason, setEditingSeason] = useState(null);
  const [isCreatingNewSeason, setIsCreatingNewSeason] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [seasonToDelete, setSeasonToDelete] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const contentType = seriesMode ? 'series' : 'anime';
  const contentItems = seriesMode ? series : animes;
  const contentLabel = seriesMode ? 'Serie' : 'Anime';

  useEffect(() => {
    if (seriesMode) {
      dispatch(fetchSeries());
    } else {
      dispatch(fetchAnimes());
    }
  }, [dispatch, seriesMode]);

  useEffect(() => {
    if (selectedContentId) {
      if (seriesMode) {
        dispatch(fetchSeasonsBySeriesId(selectedContentId));
      } else {
        dispatch(fetchSeasonsByAnimeId(selectedContentId));
      }
    }
  }, [selectedContentId, dispatch, seriesMode]);

  const filteredSeasons = seasons.filter(season =>
    season.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSeasons = filteredSeasons.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredSeasons.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleContentChange = (e) => {
    setSelectedContentId(e.target.value);
    setCurrentPage(1);
  };

  const handleEdit = (season) => {
    console.log(`Temporada seleccionada para editar:`, season);
    // Asegurarnos de que todos los campos necesarios estén presentes antes de editar
    const seasonToEdit = {
      ...season,
      // Asegurarse que contentId es el ID del contenido seleccionado
      contentId: season.contentId || parseInt(selectedContentId, 10),
      // Convertir la fecha a formato ISO si existe
      releaseDate: season.releaseDate ? new Date(season.releaseDate).toISOString().split('T')[0] : '',
      // Asegurarse que seasonNumber sea un string para el formulario
      seasonNumber: season.seasonNumber?.toString() || '',
      // Asegurarse que year sea un string para el formulario
      year: season.year?.toString() || new Date().getFullYear().toString()
    };
    
    setEditingSeason(seasonToEdit);
    setIsCreatingNewSeason(false);
    setIsEditModalOpen(true);
  };

  const handleAddNewSeason = () => {
    if (!selectedContentId) {
      setErrorMessage(`Debes seleccionar ${seriesMode ? 'una serie' : 'un anime'} para añadir una temporada`);
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
        setErrorMessage('');
      }, 3000);
      return;
    }
    
    // Crear una temporada nueva vacía con el ID del contenido seleccionado
    const emptySeason = {
      title: '',
      description: '',
      seasonNumber: '',
      posterUrl: '',
      releaseDate: '',
      year: new Date().getFullYear(),
      contentId: selectedContentId,
      contentType: contentType
    };
    
    setEditingSeason(emptySeason);
    setIsCreatingNewSeason(true);
    setIsEditModalOpen(true);
  };

  const handleSaveSeason = async (data) => {
    try {
      if (isCreatingNewSeason) {
        // Solución: enviar directamente el objeto completo con contentId incluido
        const newSeasonData = {
          title: data.seasonData.title,
          description: data.seasonData.description,
          seasonNumber: data.seasonData.seasonNumber,
          posterUrl: data.seasonData.posterUrl,
          releaseDate: data.seasonData.releaseDate,
          year: data.seasonData.year,
          contentType: contentType,
          contentId: parseInt(selectedContentId, 10)
        };
        
        // Usar directamente axios en lugar del thunk para depurar
        const token = localStorage.getItem('token');
        const authHeader = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
        const API_URL = process.env.REACT_APP_API_URL;
        
        console.log('Enviando datos al backend:', JSON.stringify(newSeasonData));
        const response = await axios.post(`${API_URL}/api/v1/seasons`, newSeasonData, authHeader);
        
        setErrorMessage('Temporada creada exitosamente');
        console.log('Respuesta del backend:', response.data);
      } else {
        // Actualizar temporada existente
        await dispatch(updateSeason(data)).unwrap();
        setErrorMessage('Temporada actualizada exitosamente');
      }
      
      setIsEditModalOpen(false);
      setEditingSeason(null);
      setIsCreatingNewSeason(false);
      
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
        setErrorMessage('');
      }, 3000);
      
      // Recargar las temporadas para el contenido seleccionado
      if (selectedContentId) {
        if (seriesMode) {
          dispatch(fetchSeasonsBySeriesId(selectedContentId));
        } else {
          dispatch(fetchSeasonsByAnimeId(selectedContentId));
        }
      }
    } catch (err) {
      console.error('Error al guardar temporada:', err);
      setErrorMessage('Error al guardar la temporada: ' + (err.message || ''));
      setShowError(true);
    }
  };
  
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingSeason(null);
  };

  const handleDelete = (season) => {
    setSeasonToDelete(season);
    setIsConfirmModalOpen(true);
  };
  
  const confirmDelete = async () => {
    if (!seasonToDelete) return;
    
    try {
      const resultAction = await dispatch(deleteSeason(seasonToDelete.id));
      if (deleteSeason.rejected.match(resultAction)) {
        const errorMsg = resultAction.payload.message || '';
        if (errorMsg.includes('Episode is associated') || errorMsg.includes('episodes')) {
          setErrorMessage('No se puede eliminar la temporada porque tiene episodios asociados');
        } else {
          setErrorMessage(errorMsg || 'Error al eliminar la temporada');
        }
        
        setShowError(true);
        setTimeout(() => {
          setShowError(false);
          setErrorMessage('');
        }, 8000);
      }
    } catch (err) {
      console.error('Error al eliminar:', err);
      setErrorMessage('Error al eliminar la temporada');
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
        setErrorMessage('');
      }, 8000);
    } finally {
      setIsConfirmModalOpen(false);
      setSeasonToDelete(null);
    }
  };
  
  const cancelDelete = () => {
    setIsConfirmModalOpen(false);
    setSeasonToDelete(null);
  };

  if (status === 'loading') {
    return <div className="text-center p-4">Cargando temporadas...</div>;
  }

  if (status === 'failed') {
    return <div className="text-center p-4 text-red-500">Error: {error}</div>;
  }

  return (
    <div className="space-y-4">
      {showError && (
        <div className="bg-red-600 text-white p-4 rounded-md mb-4 text-center font-bold">
          {errorMessage}
        </div>
      )}
      
      {/* Modal de confirmación */}
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        title="Confirmar eliminación de temporada"
        message={
          seasonToDelete 
            ? `¿Estás seguro de que deseas eliminar la temporada "${seasonToDelete.title}"?\n\nRecuerda que si tiene episodios asociados, la eliminación no será posible.`
            : "¿Estás seguro de que deseas eliminar esta temporada?"
        }
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
      
      {/* Modal de edición */}
      <EditSeasonModal 
        isOpen={isEditModalOpen}
        season={editingSeason}
        contentItems={contentItems}
        contentType={contentType}
        onClose={closeEditModal}
        onSave={handleSaveSeason}
      />
      
      <div className="flex items-center mb-4 space-x-4">
        <div className="flex-1">
          <label htmlFor="content-select" className="block text-sm font-medium text-gray-300">
            Seleccionar {contentLabel}
          </label>
          <select
            id="content-select"
            value={selectedContentId}
            onChange={handleContentChange}
            className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-800 text-gray-300"
          >
            <option value="">Selecciona {seriesMode ? 'una serie' : 'un anime'}</option>
            {contentItems.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="search" className="block text-sm font-medium text-gray-300">
            Buscar
          </label>
          <input
            id="search"
            type="text"
            placeholder="Buscar temporadas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mt-1 border border-gray-600 rounded-md px-4 py-2 w-full bg-gray-800 text-gray-300"
          />
        </div>
        
        <button 
          onClick={handleAddNewSeason}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors duration-200"
        >
          <Plus className="h-5 w-5" />
          Añadir Temporada
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-gray-900 divide-y divide-gray-700">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">{contentLabel}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Número</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Título</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Episodios</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Fecha</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody className="bg-gray-800 divide-y divide-gray-700">
            {selectedContentId ? (
              currentSeasons.length > 0 ? (
                currentSeasons.map(season => {
                  const content = contentItems.find(c => c.id === parseInt(selectedContentId));
                  return (
                    <tr key={season.id} className="hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-gray-300">{season.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-white font-medium">{content?.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-300">{season.seasonNumber}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-4 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-blue-800 text-blue-100">
                          {season.title}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-300">{season.episodeCount}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-300">
                        {new Date(season.releaseDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap space-x-2">
                        <button
                          onClick={() => handleEdit(season)}
                          className="text-blue-400 hover:text-blue-200"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(season)}
                          className="text-red-400 hover:text-red-200"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-white">
                    No hay temporadas disponibles para {seriesMode ? 'esta serie' : 'este anime'}
                  </td>
                </tr>
              )
            ) : (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-white">
                  Seleccione {seriesMode ? 'una serie' : 'un anime'} para ver sus temporadas
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center mt-4">
          <nav>
            <ul className="flex">
              {(() => {
                const pages = [];
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(
                    <button
                      key={i}
                      onClick={() => handlePageChange(i)}
                      className={`mx-1 px-3 py-1 rounded ${
                        currentPage === i
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {i}
                    </button>
                  );
                }
                return pages;
              })()}
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default SeasonTable;
